import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { INews_Response, IPaginationProps } from '@shared/constants/models.const';
import { SearchInputComponent } from '@shared/components/search-input/search-input.component';
import { FormsModule } from '@angular/forms';
import { ContentService } from '../../../services/content.service';
import { BehaviorSubject, debounceTime, filter, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaginatorComponent } from '@shared/components/paginator/paginator.component';
import { DEFAULT_PAGINATION } from '@shared/constants/enums.const';
import {
  E_COLLECTION_FOLDER_TYPES,
  ESortDirection,
  ICollectionApiResponse,
  ICollectionItem,
} from '@shared/components/collections/collection.model';
import { NewsSectionComponent } from '../../../pages/home/components/news-section/news-section.component';
import {
  PublicationCardComponent,
} from '../../../pages/home/components/latest-publications-section/components/publication-card/publication-card.component';
import { SimpleCardComponent } from '@shared/components/collections/components/simple-card/simple-card.component';
import { SortComponent } from '@shared/components/collections/components/sort/sort.component';
import { HorizontalDividerComponent } from '@shared/components/horizontal-divider/horizontal-divider.component';

@Component({
  selector: 'app-collections',
  imports: [
    SearchInputComponent,
    FormsModule,
    PaginatorComponent,
    NewsSectionComponent,
    PublicationCardComponent,
    SimpleCardComponent,
    SortComponent,
    HorizontalDividerComponent,
  ],
  standalone: true,
  templateUrl: './collections.component.html',
  styleUrl: './collections.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionsComponent implements OnInit {
  pageData = input.required<INews_Response>();
  folderID = computed(() => this.pageData().id);

  hasCollection = computed(() => {
    return (this.totalRecords() && this.collectionTitle()) || (this.collectionTitle() && this.keywordModel());
  });

  hasNoResults = computed(() => {
    return this.collectionTitle() && this.keywordModel() && !this.collectionItems().length;
  });

  keywordModel = model('');
  take = signal(5);
  skip = signal(0);
  sortModel = signal({
    sortField: 'title',
    sortOrder: ESortDirection.Asc,
  });
  params = computed(() => {
    const resultParam: any = {
      skip: this.skip(),
      take: this.take(),
      ...this.sortModel(),
    };
    if (this.keywordModel()) {
      resultParam.keyword = this.keywordModel();
    }
    return resultParam;
  });


  collection = signal<ICollectionApiResponse | null>(null);
  totalRecords = computed(() => this.collection()?.data?.totalCount || 0);
  collectionItems = computed<ICollectionItem[]>(() => {
    return this.collection()?.data?.items || [];
  });
  collectionTitle = computed(() => this.collection()?.collectionName);

  $keyword = new BehaviorSubject<string>('INIT');
  $paramsChange = new Subject<void>();
  protected readonly TYPES = E_COLLECTION_FOLDER_TYPES;
  private readonly CONTENT = inject(ContentService);
  private readonly _DR = inject(DestroyRef);

  ngOnInit() {
    this.paramsChangeHandler();
    this.searchTextChangeHandler();
    this.$paramsChange.next();
  }


  searchInputChange() {
    if (this.keywordModel() !== this.$keyword.value) {
      this.take.set(DEFAULT_PAGINATION.take);
      this.skip.set(DEFAULT_PAGINATION.skip);
    }
    this.$keyword.next(this.keywordModel());
  }

  paginationChange(props: IPaginationProps) {
    this.take.set(props.take);
    this.skip.set(props.skip);
    this.$paramsChange.next();
  }

  paramsChangeHandler() {
    this.$paramsChange.pipe(
      takeUntilDestroyed(this._DR),
      switchMap(() => this.CONTENT.getCollectionInfo(this.folderID(), this.params())),
    ).subscribe(res => {
      this.collection.set(res.collectionName ? res : null);
    });
  }

  searchTextChangeHandler() {
    this.$keyword
      .pipe(
        takeUntilDestroyed(this._DR),
        debounceTime(500),
        filter(value => value !== 'INIT'),
        filter(value => {
          return !value || value.length > 2;
        }),
      )
      .subscribe(() => this.$paramsChange.next());
  }

  sortChange(props: any) {
    this.sortModel.set(props);
    this.$paramsChange.next();
  }
}
